import React, { useEffect, useRef } from 'react';
import { EnvelopeIcon, PhoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Conversation } from '../../../types/conversation';
import { ConversationLabel, Label } from '../../../types/label';
import { CHANNEL_TYPE_LABELS } from '../../../types/atendimentoEnums';
import { getConversationLabelDisplay } from '../../../utils/leadTemperatureDisplay';
import { WorkspaceThemeClasses } from './workspaceTheme';

interface WorkspaceContactPanelProps {
  theme: WorkspaceThemeClasses;
  conversation: Conversation | null;
  isOpen: boolean;
  conversationLabels: ConversationLabel[];
  unappliedLabels: Label[];
  selectedLabelId: string;
  labelUpdating: boolean;
  onClose: () => void;
  onSelectedLabelChange: (labelId: string) => void;
  onApplyLabel: () => void;
  onRemoveLabel: (labelId: string) => void;
}

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '?';
  }

  if (parts.length === 1)
  {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const WorkspaceContactPanel: React.FC<WorkspaceContactPanelProps> = ({
  theme,
  conversation,
  isOpen,
  conversationLabels,
  unappliedLabels,
  selectedLabelId,
  labelUpdating,
  onClose,
  onSelectedLabelChange,
  onApplyLabel,
  onRemoveLabel,
}) => {
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isOpen)
    {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element))
      {
        return;
      }

      if (panelRef.current?.contains(target))
      {
        return;
      }

      if (target.closest('[data-contact-panel-toggle]'))
      {
        return;
      }

      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !conversation)
  {
    return null;
  }

  return (
    <aside
      ref={panelRef}
      id="workspace-contact-panel"
      className={`flex flex-col overflow-hidden ${theme.border} ${theme.surface} max-lg:absolute max-lg:inset-0 max-lg:z-30 max-lg:w-full lg:w-80 lg:flex-shrink-0 lg:border-l`}
      aria-label="Informações do contato"
    >
      <div className={`relative px-4 py-5 text-center border-b ${theme.border}`}>
        <button
          type="button"
          onClick={onClose}
          className={`absolute right-2 top-2 rounded-md p-1 ${theme.muted} hover:bg-gray-100`}
          aria-label="Ocultar informações do contato"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="mx-auto h-14 w-14 rounded-full bg-blue-600 text-white text-lg font-bold grid place-items-center">
          {initialsFromName(conversation.contactName)}
        </div>
        <p className={`mt-2.5 text-sm font-bold ${theme.text}`}>{conversation.contactName}</p>
        <p className={`text-xs mt-0.5 ${theme.muted}`}>
          {conversation.supportGroupName ?? conversation.inboxName}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>E-mail</p>
          <p className={`mt-1 text-sm flex items-center gap-2 ${theme.text}`}>
            <EnvelopeIcon className={`h-4 w-4 ${theme.muted}`} />
            {conversation.contactEmail || '—'}
          </p>
        </div>

        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Telefone</p>
          <p className={`mt-1 text-sm flex items-center gap-2 ${theme.text}`}>
            <PhoneIcon className={`h-4 w-4 ${theme.muted}`} />
            {conversation.contactPhone || '—'}
          </p>
        </div>

        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Canal</p>
          <p className={`mt-1 text-sm ${theme.text}`}>
            {CHANNEL_TYPE_LABELS[conversation.channelType]} · {conversation.inboxName}
          </p>
        </div>

        {conversation.contactIdentifier && (
          <div>
            <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Identificador</p>
            <p className={`mt-1 text-sm ${theme.text}`}>{conversation.contactIdentifier}</p>
          </div>
        )}

        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Equipe</p>
          <p className={`mt-1 text-sm ${theme.text}`}>
            {conversation.teamName || 'Sem equipe'}
          </p>
        </div>

        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Etiquetas</p>
          <div className="mt-2 flex flex-wrap gap-1.5 min-h-[1.5rem]">
            {conversationLabels.length === 0 ? (
              <span className={`text-xs ${theme.muted}`}>Nenhuma etiqueta</span>
            ) : (
              conversationLabels.map((item) => {
                const display = getConversationLabelDisplay(item);

                return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onRemoveLabel(item.labelId)}
                  disabled={labelUpdating}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full text-white hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: display.color }}
                  title={item.appliedByBot ? 'Aplicada por IA — clique para remover' : 'Clique para remover'}
                >
                  {display.text}
                  <span aria-hidden>×</span>
                </button>
                );
              })
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <select
              value={selectedLabelId}
              onChange={(event) => onSelectedLabelChange(event.target.value)}
              className={`flex-1 rounded-md border px-2 py-1 text-xs ${theme.input}`}
              disabled={labelUpdating || unappliedLabels.length === 0}
            >
              <option value="">Adicionar...</option>
              {unappliedLabels.map((label) => {
                const display = getConversationLabelDisplay({
                  labelName: label.name,
                  labelColor: label.color,
                  labelDescription: label.description,
                });

                return (
                  <option key={label.id} value={label.id}>{display.text}</option>
                );
              })}
            </select>
            <button
              type="button"
              onClick={onApplyLabel}
              disabled={labelUpdating || !selectedLabelId}
              className={`text-xs font-semibold px-2.5 py-1 rounded-md ${theme.accentBtn} disabled:opacity-50`}
            >
              Aplicar
            </button>
          </div>
        </div>

        <div>
          <p className={`text-[10.5px] font-bold uppercase tracking-wide ${theme.faint}`}>Responsável</p>
          <p className={`mt-1 text-sm ${theme.text}`}>
            {conversation.assigneeName || 'Sem responsável'}
          </p>
        </div>
      </div>
    </aside>
  );
};
